import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ListItemText,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTheme } from "../../context/ThemeContext";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import styles from "./LearningDialog.module.scss";

const DEFAULT_CONFIG = {
  enabled: false,
  mode: "always",
  stores: {
    user_profile: { enabled: true, mode: "always" },
    user_memory: { enabled: true, mode: "always" },
    session_context: { enabled: false, enable_planning: false },
    entity_memory: { enabled: false, mode: "always" },
    learned_knowledge: { enabled: false, mode: "agentic" },
    decision_log: { enabled: false, mode: "agentic" },
  },
};

const mergeLearningConfig = (config) => ({
  ...DEFAULT_CONFIG,
  ...(config || {}),
  stores: {
    user_profile: {
      ...DEFAULT_CONFIG.stores.user_profile,
      ...(config?.stores?.user_profile || {}),
    },
    user_memory: {
      ...DEFAULT_CONFIG.stores.user_memory,
      ...(config?.stores?.user_memory || {}),
    },
    session_context: {
      ...DEFAULT_CONFIG.stores.session_context,
      ...(config?.stores?.session_context || {}),
    },
    entity_memory: {
      ...DEFAULT_CONFIG.stores.entity_memory,
      ...(config?.stores?.entity_memory || {}),
    },
    learned_knowledge: {
      ...DEFAULT_CONFIG.stores.learned_knowledge,
      ...(config?.stores?.learned_knowledge || {}),
    },
    decision_log: {
      ...DEFAULT_CONFIG.stores.decision_log,
      ...(config?.stores?.decision_log || {}),
    },
  },
});

const LearningDialog = ({ open, onClose, onSave, initialData, modeOptions = [] }) => {
  const { isDarkMode } = useTheme();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? "dark" : "light",
      primary: {
        main: "#e36250 !important",
      },
      secondary: {
        main: "#8b5cf6",
      },
    },
  });

  const [formData, setFormData] = useState(mergeLearningConfig(initialData));

  useEffect(() => {
    if (open) {
      setFormData(mergeLearningConfig(initialData));
    }
  }, [open, initialData]);

  const handleSave = () => {
    onSave(formData);
    onClose(formData);
  };

  const handleCancel = () => {
    onClose(formData);
  };

  const handleStoreUpdate = (storeKey, updates) => {
    setFormData((prev) => ({
      ...prev,
      stores: {
        ...prev.stores,
        [storeKey]: {
          ...(prev.stores?.[storeKey] || {}),
          ...updates,
        },
      },
    }));
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Dialog
        open={open}
        onClose={() => onClose(formData)}
        maxWidth={false}
        fullWidth
        scroll="paper"
        className={styles.dialog}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: "rgba(24, 24, 27, 0.4)",
              backdropFilter: "blur(2px)",
            },
          },
        }}
        PaperProps={{
          className: styles.dialogPaper,
        }}
      >
        <DialogTitle className={styles.dialogTitle} component="div">
          <Box className={styles.titleRow}>
            <Typography component="h3" variant="h6" className={styles.title}>
              Configure Learning
            </Typography>
          </Box>
          <IconButton
            onClick={() => onClose(formData)}
            size="small"
            className={styles.closeButton}
            aria-label="Close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Box className={styles.formContainer}>
            <Box className={styles.section}>
              <Typography variant="body2" className={styles.sectionLabel}>
                Default Learning Mode
              </Typography>
              <FormControl fullWidth size="small" disabled={!formData.enabled}>
                <InputLabel id="learning-default-mode-label">Mode</InputLabel>
                <Select
                  labelId="learning-default-mode-label"
                  label="Mode"
                  value={formData.mode}
                  onChange={(event) => {
                    setFormData((prev) => ({
                      ...prev,
                      mode: event.target.value,
                    }));
                  }}
                >
                  {modeOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                      disabled={Boolean(option.disabled)}
                    >
                      <ListItemText primary={option.label} secondary={option.helper} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box className={styles.section}>
              <Typography variant="body2" className={styles.sectionLabel}>
                Stores
              </Typography>

              <Box className={styles.storeRow}>
                <Box className={styles.storeMeta}>
                  <Typography variant="body2">User Profile</Typography>
                  <Typography variant="caption" className={styles.sectionHint}>
                    Structured user facts (name, company, role)
                  </Typography>
                </Box>
                <Box className={styles.storeControls}>
                  <FormControl size="small" sx={{ minWidth: 150 }} disabled={!formData.enabled || !formData.stores.user_profile.enabled}>
                    <InputLabel id="learning-user-profile-mode-label">Mode</InputLabel>
                    <Select
                      labelId="learning-user-profile-mode-label"
                      label="Mode"
                      value={formData.stores.user_profile.mode}
                      onChange={(event) => {
                        handleStoreUpdate("user_profile", { mode: event.target.value });
                      }}
                    >
                      {modeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={Boolean(option.disabled)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Switch
                    checked={formData.stores.user_profile.enabled}
                    onChange={(event) => {
                      handleStoreUpdate("user_profile", { enabled: event.target.checked });
                    }}
                    disabled={!formData.enabled}
                  />
                </Box>
              </Box>

              <Box className={styles.storeRow}>
                <Box className={styles.storeMeta}>
                  <Typography variant="body2">User Memory</Typography>
                  <Typography variant="caption" className={styles.sectionHint}>
                    Unstructured observations about the user
                  </Typography>
                </Box>
                <Box className={styles.storeControls}>
                  <FormControl size="small" sx={{ minWidth: 150 }} disabled={!formData.enabled || !formData.stores.user_memory.enabled}>
                    <InputLabel id="learning-user-memory-mode-label">Mode</InputLabel>
                    <Select
                      labelId="learning-user-memory-mode-label"
                      label="Mode"
                      value={formData.stores.user_memory.mode}
                      onChange={(event) => {
                        handleStoreUpdate("user_memory", { mode: event.target.value });
                      }}
                    >
                      {modeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={Boolean(option.disabled)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Switch
                    checked={formData.stores.user_memory.enabled}
                    onChange={(event) => {
                      handleStoreUpdate("user_memory", { enabled: event.target.checked });
                    }}
                    disabled={!formData.enabled}
                  />
                </Box>
              </Box>

              <Box className={styles.storeRow}>
                <Box className={styles.storeMeta}>
                  <Typography variant="body2">Session Context</Typography>
                  <Typography variant="caption" className={styles.sectionHint}>
                    Goals, plans, and progress for the session
                  </Typography>
                </Box>
                <Box className={styles.storeControls}>
                  <Box className={styles.planningToggle}>
                    <Typography variant="caption" className={styles.sectionHint}>
                      Planning
                    </Typography>
                    <Switch
                      checked={formData.stores.session_context.enable_planning}
                      onChange={(event) => {
                        handleStoreUpdate("session_context", { enable_planning: event.target.checked });
                      }}
                      disabled={!formData.enabled || !formData.stores.session_context.enabled}
                    />
                  </Box>
                  <Switch
                    checked={formData.stores.session_context.enabled}
                    onChange={(event) => {
                      handleStoreUpdate("session_context", { enabled: event.target.checked });
                    }}
                    disabled={!formData.enabled}
                  />
                </Box>
              </Box>

              <Box className={styles.storeRow}>
                <Box className={styles.storeMeta}>
                  <Typography variant="body2">Entity Memory</Typography>
                  <Typography variant="caption" className={styles.sectionHint}>
                    Facts about companies, projects, and people
                  </Typography>
                </Box>
                <Box className={styles.storeControls}>
                  <FormControl size="small" sx={{ minWidth: 150 }} disabled={!formData.enabled || !formData.stores.entity_memory.enabled}>
                    <InputLabel id="learning-entity-memory-mode-label">Mode</InputLabel>
                    <Select
                      labelId="learning-entity-memory-mode-label"
                      label="Mode"
                      value={formData.stores.entity_memory.mode}
                      onChange={(event) => {
                        handleStoreUpdate("entity_memory", { mode: event.target.value });
                      }}
                    >
                      {modeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={Boolean(option.disabled)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Switch
                    checked={formData.stores.entity_memory.enabled}
                    onChange={(event) => {
                      handleStoreUpdate("entity_memory", { enabled: event.target.checked });
                    }}
                    disabled={!formData.enabled}
                  />
                </Box>
              </Box>

              <Box className={styles.storeRow}>
                <Box className={styles.storeMeta}>
                  <Typography variant="body2">Learned Knowledge</Typography>
                  <Typography variant="caption" className={styles.sectionHint}>
                    Reusable team insights (shared)
                  </Typography>
                </Box>
                <Box className={styles.storeControls}>
                  <FormControl size="small" sx={{ minWidth: 150 }} disabled={!formData.enabled || !formData.stores.learned_knowledge.enabled}>
                    <InputLabel id="learning-learned-knowledge-mode-label">Mode</InputLabel>
                    <Select
                      labelId="learning-learned-knowledge-mode-label"
                      label="Mode"
                      value={formData.stores.learned_knowledge.mode}
                      onChange={(event) => {
                        handleStoreUpdate("learned_knowledge", { mode: event.target.value });
                      }}
                    >
                      {modeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={Boolean(option.disabled)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Switch
                    checked={formData.stores.learned_knowledge.enabled}
                    onChange={(event) => {
                      handleStoreUpdate("learned_knowledge", { enabled: event.target.checked });
                    }}
                    disabled={!formData.enabled}
                  />
                </Box>
              </Box>

              <Box className={styles.storeRow}>
                <Box className={styles.storeMeta}>
                  <Typography variant="body2">Decision Log</Typography>
                  <Typography variant="caption" className={styles.sectionHint}>
                    Decision tracking and audit trail
                  </Typography>
                </Box>
                <Box className={styles.storeControls}>
                  <FormControl size="small" sx={{ minWidth: 150 }} disabled={!formData.enabled || !formData.stores.decision_log.enabled}>
                    <InputLabel id="learning-decision-log-mode-label">Mode</InputLabel>
                    <Select
                      labelId="learning-decision-log-mode-label"
                      label="Mode"
                      value={formData.stores.decision_log.mode}
                      onChange={(event) => {
                        handleStoreUpdate("decision_log", { mode: event.target.value });
                      }}
                    >
                      {modeOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          disabled={Boolean(option.disabled)}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Switch
                    checked={formData.stores.decision_log.enabled}
                    onChange={(event) => {
                      handleStoreUpdate("decision_log", { enabled: event.target.checked });
                    }}
                    disabled={!formData.enabled}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleCancel}
            variant="text"
            className={styles.cancelButton}
          >
            Close
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className={styles.saveButton}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default LearningDialog;
